/**
 * Schritt 4: Monte-Carlo-Simulation
 * Fokussiert auf Monte-Carlo-Simulationen für Portfolio-Strategien
 */

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
} from '@cloudscape-design/components';

import { AppZustand, SimulationOptions, SteuerParameter } from '@/types';
import { CrisisDefinitionType } from '@/types/simulation';
import { CombinedMonteCarloEngine } from '../services/CombinedMonteCarloEngine';
import { SIMULATION_CONFIG } from '../config/simulation';
import { SimpleSimulationControls } from '../components/simulation/SimpleSimulationControls';
import { MonteCarloTab } from '../components/simulation/MonteCarloTab';

interface Schritt4Props {
  steuerParameter: SteuerParameter | null;
  simulationOptions: SimulationOptions;
  crisisDefinition: string;
  onSimulationOptionsUpdate: (options: Partial<SimulationOptions>) => void;
  onCrisisDefinitionUpdate: (crisisDefinition: string) => void;
  simulationState: AppZustand['simulationState'];
  onSimulationStateUpdate: (updates: Partial<AppZustand['simulationState']>) => void;
}

/**
 * Komponente für die Monte-Carlo-Simulation
 */
const Schritt4: React.FC<Schritt4Props> = ({
  steuerParameter,
  simulationOptions,
  crisisDefinition,
  onSimulationOptionsUpdate,
  onCrisisDefinitionUpdate,
  simulationState,
  onSimulationStateUpdate,
}) => {
  // Extract options from centralized state
  const { inflationOption, investmentReturnOption, tagesgeldOption, zinstopfOption } =
    simulationOptions;

  // Create handlers that update centralized state
  const setCrisisDefinition = (value: string) => onCrisisDefinitionUpdate(value);
  const setInflationOption = (value: string) =>
    onSimulationOptionsUpdate({ inflationOption: value });
  const setInvestmentReturnOption = (value: string) =>
    onSimulationOptionsUpdate({ investmentReturnOption: value });
  const setTagesgeldOption = (value: string) =>
    onSimulationOptionsUpdate({ tagesgeldOption: value });
  const setZinstopfOption = (value: string) => onSimulationOptionsUpdate({ zinstopfOption: value });

  // Extract simulation state from global state
  const {
    monteCarloResults,
    monteCarloStats,
    isRunningMonteCarlo,
    monteCarloProgress,
    monteCarloError,
  } = simulationState;

  // State to store the current MonteCarloEngine instance for drill-down analysis
  const [currentMonteCarloEngine, setCurrentMonteCarloEngine] = useState<CombinedMonteCarloEngine | null>(null);

  // Create Monte Carlo simulation runner
  const runMonteCarloSimulation = async () => {
    if (!steuerParameter) {return;}

    onSimulationStateUpdate({
      isRunningMonteCarlo: true,
      monteCarloError: null,
      monteCarloProgress: 0,
      monteCarloResults: [],
      monteCarloStats: null,
    });

    try {
      // Clear master seeds and static cache for fresh crypto-based randomization in Monte Carlo
      const { RateCalculationService } = await import('../services/RateCalculationService');
      const { SharedRateGenerator } = await import('../services/SharedRateGenerator');
      RateCalculationService.clearMasterSeeds();
      SharedRateGenerator.clearStaticCache();

      const engine = new CombinedMonteCarloEngine(
        steuerParameter,
        {
          inflationOption,
          investmentReturnOption,
          tagesgeldOption,
          zinstopfOption,
        },
        {
          inflationOption,
          investmentReturnOption,
        },
        crisisDefinition as CrisisDefinitionType
      );

      // Store the engine instance for drill-down analysis
      setCurrentMonteCarloEngine(engine);

      const iterations = steuerParameter?.monteCarloIterations || SIMULATION_CONFIG.MONTE_CARLO.DEFAULT_ITERATIONS;
      const results = await engine.runCombinedMonteCarlo(
        iterations, // Number of iterations from steuerParameter or default
        (progress: number) => {
          onSimulationStateUpdate({ monteCarloProgress: progress });
        }
      );

      // Calculate stats from results
      const stats = engine.calculateCombinedStatistics(results);

      // First update to show 100% progress
      onSimulationStateUpdate({
        monteCarloProgress: 100,
      });

      // Brief delay to allow user to see 100% completion
      await new Promise(resolve => setTimeout(resolve, 800));

      // Then hide progress bar and show results
      onSimulationStateUpdate({
        monteCarloResults: results,
        monteCarloStats: stats,
        isRunningMonteCarlo: false,
        monteCarloProgress: 100,
      });
    } catch (error) {
      onSimulationStateUpdate({
        isRunningMonteCarlo: false,
        monteCarloError: error as any,
        monteCarloProgress: 0,
      });
    }
  };

  // Monte Carlo Export handlers
  const handleExportMonteCarlo3Topf = () => {
    if (!monteCarloResults || monteCarloResults.length === 0 || !monteCarloStats) {return;}

    const exportData = {
      exportInfo: {
        title: '3-Töpfe Monte-Carlo-Simulation Export',
        exportDate: new Date().toISOString(),
        simulationType: '3-Topf Monte-Carlo-Simulation',
        totalIterations: monteCarloResults.length,
        analysisType: 'Comprehensive Monte Carlo Risk Analysis',
      },
      simulationParameters: {
        steuerParameter,
        simulationOptions: {
          inflationOption,
          investmentReturnOption,
          tagesgeldOption,
          zinstopfOption,
        },
        crisisDefinition,
        actualValues: {
          retirementAge: `${steuerParameter?.retirementAge} Jahre`,
          berechnungBisLebensalter: `${steuerParameter?.berechnungBisLebensalter} Jahre`,
          zinssatzVerbrauchstopf: `${steuerParameter?.zinssatzVerbrauchstopf}%`,
          zinssatzZinstopf: `${steuerParameter?.zinssatzZinstopf}%`,
          renditeInvestitionstopf: `${steuerParameter?.renditeInvestitionstopf}%`,
          inflationsrate: `${steuerParameter?.inflationsrate}%`,
          krisendefinition: (() => {
            switch (crisisDefinition) {
              case 'negative-return':
                return 'Krise bei negativer Jahresrendite';
              case 'kapitalindex-drawdown':
                return 'Krise bei Kapitalindex-Rückgang - Ende erst bei Erholung';
              default:
                return 'Krise bei negativer Jahresrendite';
            }
          })(),
        },
      },
      monteCarloStatistics: {
        threeTopf: {
          successRate: monteCarloStats.threeTopf.successRate,
          averageFinalBalance: monteCarloStats.threeTopf.averageFinalBalance,
          medianFinalBalance: monteCarloStats.threeTopf.medianFinalBalance,
          averageYearsFunded: monteCarloStats.threeTopf.averageYearsFunded,
          percentile5: monteCarloStats.threeTopf.percentile5,
          percentile95: monteCarloStats.threeTopf.percentile95,
        },
        analysis: {
          totalIterations: monteCarloResults.length,
          recommendedStrategy:
            monteCarloStats.threeTopf.successRate >= monteCarloStats.oneTopf.successRate
              ? '3-Töpfe'
              : '1-Topf',
          riskAssessment:
            monteCarloStats.threeTopf.successRate >= 80
              ? 'Niedrig'
              : monteCarloStats.threeTopf.successRate >= 60
                ? 'Mittel'
                : 'Hoch',
        },
      },
      iterationResults: monteCarloResults.map((result, index) => ({
        iteration: index + 1,
        threeTopfStrategy: {
          finalBalance: result.threeTopf.finalBalance,
          success: result.threeTopf.successfulStrategy,
          totalWithdrawals: result.threeTopf.totalWithdrawals,
          // -1: jahresergebnisse enthält Jahr 0 (Rentenbeginn); finanzierte Jahre nach
          // Rentenbeginn = Einträge - 1 (konsistent mit averageYearsFunded in der Statistik)
          yearsFunded: result.threeTopf.jahresergebnisse.length - 1,
          yearlyBalances: result.threeTopf.yearlyBalances,
        },
      })),
      summary: {
        exportedIterations: monteCarloResults.length,
        strategyFocus: '3-Töpfe Strategy Monte Carlo Analysis',
        riskMetrics: {
          successRate: `${monteCarloStats.threeTopf.successRate.toFixed(1)}%`,
          averageOutcome: `${Math.round(monteCarloStats.threeTopf.averageFinalBalance).toLocaleString('de-DE')} €`,
          medianOutcome: `${Math.round(monteCarloStats.threeTopf.medianFinalBalance).toLocaleString('de-DE')} €`,
          worstCase5Percent: `${Math.round(monteCarloStats.threeTopf.percentile5).toLocaleString('de-DE')} €`,
          bestCase5Percent: `${Math.round(monteCarloStats.threeTopf.percentile95).toLocaleString('de-DE')} €`,
          averageYearsFunded: monteCarloStats.threeTopf.averageYearsFunded,
        },
      },
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `3-topf-monte-carlo-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportMonteCarlo1Topf = () => {
    if (!monteCarloResults || monteCarloResults.length === 0 || !monteCarloStats) {return;}

    const exportData = {
      exportInfo: {
        title: '1-Topf Monte-Carlo-Simulation Export',
        exportDate: new Date().toISOString(),
        simulationType: '1-Topf Monte-Carlo-Simulation',
        totalIterations: monteCarloResults.length,
        analysisType: 'Comprehensive Monte Carlo Risk Analysis',
      },
      simulationParameters: {
        steuerParameter,
        simulationOptions: {
          inflationOption,
          investmentReturnOption,
        },
        crisisDefinition,
        actualValues: {
          retirementAge: `${steuerParameter?.retirementAge} Jahre`,
          berechnungBisLebensalter: `${steuerParameter?.berechnungBisLebensalter} Jahre`,
          renditeInvestitionstopf: `${steuerParameter?.renditeInvestitionstopf}%`,
          inflationsrate: `${steuerParameter?.inflationsrate}%`,
          krisendefinition: (() => {
            switch (crisisDefinition) {
              case 'negative-return':
                return 'Krise bei negativer Jahresrendite';
              case 'kapitalindex-drawdown':
                return 'Krise bei Kapitalindex-Rückgang - Ende erst bei Erholung';
              default:
                return 'Krise bei negativer Jahresrendite';
            }
          })(),
        },
      },
      monteCarloStatistics: {
        oneTopf: {
          successRate: monteCarloStats.oneTopf.successRate,
          averageFinalBalance: monteCarloStats.oneTopf.averageFinalBalance,
          medianFinalBalance: monteCarloStats.oneTopf.medianFinalBalance,
          averageYearsFunded: monteCarloStats.oneTopf.averageYearsFunded,
          percentile5: monteCarloStats.oneTopf.percentile5,
          percentile95: monteCarloStats.oneTopf.percentile95,
        },
        analysis: {
          totalIterations: monteCarloResults.length,
          recommendedStrategy:
            monteCarloStats.oneTopf.successRate >= monteCarloStats.threeTopf.successRate
              ? '1-Topf'
              : '3-Töpfe',
          riskAssessment:
            monteCarloStats.oneTopf.successRate >= 80
              ? 'Niedrig'
              : monteCarloStats.oneTopf.successRate >= 60
                ? 'Mittel'
                : 'Hoch',
        },
      },
      iterationResults: monteCarloResults.map((result, index) => ({
        iteration: index + 1,
        oneTopfStrategy: {
          finalBalance: result.oneTopf.finalBalance,
          success: result.oneTopf.successfulStrategy,
          totalWithdrawals: result.oneTopf.totalWithdrawals,
          // -1: jahresergebnisse enthält Jahr 0 (Rentenbeginn); finanzierte Jahre nach
          // Rentenbeginn = Einträge - 1 (konsistent mit averageYearsFunded in der Statistik)
          yearsFunded: result.oneTopf.jahresergebnisse.length - 1,
          yearlyBalances: result.oneTopf.yearlyBalances,
        },
      })),
      summary: {
        exportedIterations: monteCarloResults.length,
        strategyFocus: '1-Topf Strategy Monte Carlo Analysis',
        riskMetrics: {
          successRate: `${monteCarloStats.oneTopf.successRate.toFixed(1)}%`,
          averageOutcome: `${Math.round(monteCarloStats.oneTopf.averageFinalBalance).toLocaleString('de-DE')} €`,
          medianOutcome: `${Math.round(monteCarloStats.oneTopf.medianFinalBalance).toLocaleString('de-DE')} €`,
          worstCase5Percent: `${Math.round(monteCarloStats.oneTopf.percentile5).toLocaleString('de-DE')} €`,
          bestCase5Percent: `${Math.round(monteCarloStats.oneTopf.percentile95).toLocaleString('de-DE')} €`,
          averageYearsFunded: monteCarloStats.oneTopf.averageYearsFunded,
        },
      },
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `1-topf-monte-carlo-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clearErrors = () => {
    onSimulationStateUpdate({
      simulationError: null,
      oneTopfSimulationError: null,
      monteCarloError: null,
    });
  };

  // Show validation error if parameters are missing
  if (!steuerParameter) {
    return (
      <Container
        header={
          <Header
            variant="h2"
            description="Führen Sie Monte-Carlo-Simulationen für umfassende Risikoanalysen durch."
          >
            Monte-Carlo-Simulation
          </Header>
        }
      >
        <Alert type="warning" header="Keine Parameter verfügbar">
          Bitte geben Sie zunächst die Berechnungsparameter in "Schritt 1: Individuelle-Parameter" ein, um
          Monte-Carlo-Simulationen durchführen zu können.
        </Alert>
      </Container>
    );
  }

  return (
    <Container
      header={
        <Box>
          <ColumnLayout columns={2}>
            <Header
              variant="h2"
              description="Analysieren Sie Ihre Portfolio-Strategien mit Monte-Carlo-Simulationen für realistische Risikobewertungen."
            >
              Monte-Carlo-Simulation
            </Header>
            <Box textAlign="right">
              <SpaceBetween direction="horizontal" size="s">
                <Button
                  variant="primary"
                  onClick={runMonteCarloSimulation}
                  loading={isRunningMonteCarlo}
                  disabled={isRunningMonteCarlo}
                >
                  {isRunningMonteCarlo ? 'Monte Carlo läuft...' : 'Monte-Carlo-Simulation starten'}
                </Button>

                <Button
                  onClick={() => handleExportMonteCarlo3Topf()}
                  iconName="download"
                  disabled={!monteCarloResults || monteCarloResults.length === 0}
                  variant="normal"
                >
                  3-Topf JSON Export
                </Button>

                <Button
                  onClick={() => handleExportMonteCarlo1Topf()}
                  iconName="download"
                  disabled={!monteCarloResults || monteCarloResults.length === 0}
                  variant="normal"
                >
                  1-Topf JSON Export
                </Button>
              </SpaceBetween>
            </Box>
          </ColumnLayout>
        </Box>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        {/* Global Error Display */}
        {monteCarloError && (
          <Alert type="error" header="Monte Carlo Fehler" dismissible onDismiss={clearErrors}>
            {monteCarloError.message}
          </Alert>
        )}

        {/* Simulation Controls */}
        <SimpleSimulationControls
          steuerParameter={steuerParameter}
          crisisDefinition={crisisDefinition}
          inflationOption={inflationOption}
          investmentReturnOption={investmentReturnOption}
          tagesgeldOption={tagesgeldOption}
          zinstopfOption={zinstopfOption}
          onCrisisDefinitionChange={setCrisisDefinition}
          onInflationOptionChange={setInflationOption}
          onInvestmentReturnOptionChange={setInvestmentReturnOption}
          onTagesgeldOptionChange={setTagesgeldOption}
          onZinstopfOptionChange={setZinstopfOption}
        />

        {/* Monte Carlo Progress Bar - Custom Implementation */}
        {isRunningMonteCarlo && (
          <Container>
            <SpaceBetween direction="vertical" size="s">
              <Box>
                <Box variant="h4">Monte Carlo Simulation läuft...</Box>
                <Box variant="p" color="text-body-secondary">
                  Fortschritt: {Math.round(monteCarloProgress)}% abgeschlossen (
                  {Math.round((monteCarloProgress / 100) * (steuerParameter?.monteCarloIterations || SIMULATION_CONFIG.MONTE_CARLO.DEFAULT_ITERATIONS))} von {(steuerParameter?.monteCarloIterations || SIMULATION_CONFIG.MONTE_CARLO.DEFAULT_ITERATIONS).toLocaleString('de-DE')} Iterationen)
                </Box>
              </Box>

              {/* Custom Progress Bar */}
              <div
                style={{
                  width: '100%',
                  height: '30px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  overflow: 'hidden',
                  position: 'relative',
                  boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.1)',
                }}
              >
                {/* Progress fill */}
                <div
                  style={{
                    width: `${Math.max(0, Math.min(100, monteCarloProgress))}%`,
                    height: '100%',
                    backgroundColor: '#0073bb',
                    background: 'linear-gradient(90deg, #0073bb 0%, #005a9d 100%)',
                    transition: 'width 0.3s ease-out',
                    borderRadius: '5px',
                  }}
                />

                {/* Progress text overlay */}
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: monteCarloProgress > 40 ? '#ffffff' : '#374151',
                    textShadow: monteCarloProgress > 40 ? '0 1px 2px rgba(0,0,0,0.5)' : 'none',
                    zIndex: 1,
                  }}
                >
                  {monteCarloProgress.toFixed(1)}%
                </div>
              </div>

              {/* Estimated time remaining */}
              <Box variant="small" color="text-body-secondary">
                Bitte warten Sie, während die Simulation läuft...
              </Box>
            </SpaceBetween>
          </Container>
        )}

        {/* Results */}
        <MonteCarloTab
          monteCarloResults={monteCarloResults}
          monteCarloStats={monteCarloStats}
          steuerParameter={steuerParameter}
          simulationOptions={{
            inflationOption,
            investmentReturnOption,
            tagesgeldOption,
            zinstopfOption,
          }}
          oneTopfSimulationOptions={{
            inflationOption,
            investmentReturnOption,
          }}
          crisisDefinition={crisisDefinition}
          monteCarloEngine={currentMonteCarloEngine || undefined}
        />
      </SpaceBetween>
    </Container>
  );
};

export default Schritt4;
